/**
 * Pure POPSTAR rank rules — the showbiz progression that dresses up the raw
 * {@link import('./xp').levelForXp} number. Levels run 1–256, grouped into 16
 * THEMED TIERS of 16 ranks each ("from first soundcheck to global icon"). Every
 * level has a single-word, non-violent, gender-neutral rank NAME; the final rank
 * (level 256) is the network itself — POPSTAR.
 *
 * This is a *pure* lookup shared by the UI (to draw the rank badge / level-up
 * ceremony) and — via the build's `_shared` copy of `src/core` — by the Cloud
 * Function (if it ever needs the rank label). It depends on NOTHING but the level
 * integer, holds no engine/React/Firebase imports, and is NOT part of the
 * deterministic simulation (the sim never reads XP or level), so changing a rank
 * name does NOT require a {@link import('./constants').SIM_VERSION} bump.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */

/** The lowest and highest defined rank levels. Level 256 is the capstone, POPSTAR. */
export const MIN_RANK_LEVEL = 1;
export const MAX_RANK_LEVEL = 256;
/** Ranks per tier (16 tiers × 16 = 256 levels). */
export const RANKS_PER_TIER = 16;

/** One of the 16 themed tiers, in ascending order (1 = THE SPARK … 16 = ETERNAL). */
export interface RankTier {
  /** 1-based tier number. */
  tier: number;
  /** Roman-numeral label (I … XVI), as the owner specced. */
  roman: string;
  /** The tier's display name (e.g. "The Spark", "Gold", "Eternal"). */
  name: string;
  /** A one-line flavour tagline for the tier. */
  tagline: string;
}

/** The 16 tiers, ascending. `name`/`tagline` straight from the owner's rank spec. */
export const RANK_TIERS: readonly RankTier[] = [
  { tier: 1, roman: 'I', name: 'The Spark', tagline: 'Just found the mic.' },
  { tier: 2, roman: 'II', name: 'The Demo', tagline: 'Putting the sound together.' },
  { tier: 3, roman: 'III', name: 'The Buzz', tagline: 'People are starting to talk.' },
  { tier: 4, roman: 'IV', name: 'The Stage', tagline: 'First real shows.' },
  { tier: 5, roman: 'V', name: 'The Rise', tagline: 'Momentum building.' },
  { tier: 6, roman: 'VI', name: 'The Breakout', tagline: 'Going wide.' },
  { tier: 7, roman: 'VII', name: 'The Charts', tagline: 'The numbers come in.' },
  { tier: 8, roman: 'VIII', name: 'The Spotlight', tagline: 'All eyes on you.' },
  { tier: 9, roman: 'IX', name: 'The Headliner', tagline: 'Top of the bill.' },
  { tier: 10, roman: 'X', name: 'Gold', tagline: 'Certified.' },
  { tier: 11, roman: 'XI', name: 'Platinum', tagline: 'Beyond gold.' },
  { tier: 12, roman: 'XII', name: 'Diamond', tagline: 'Rare and flawless.' },
  { tier: 13, roman: 'XIII', name: 'The Icon', tagline: 'A cultural figure.' },
  { tier: 14, roman: 'XIV', name: 'The Legend', tagline: 'All-time great.' },
  { tier: 15, roman: 'XV', name: 'The Cosmos', tagline: 'Bigger than the stage.' },
  { tier: 16, roman: 'XVI', name: 'Eternal', tagline: 'Immortal status.' },
] as const;

/**
 * The 256 rank names, level 1 → 256 (index 0 = level 1). Sixteen contiguous runs
 * of {@link RANKS_PER_TIER} map onto {@link RANK_TIERS} in order. The owner's
 * canonical list — single-word, non-violent, gender-neutral; level 256 = POPSTAR.
 */
export const RANK_NAMES: readonly string[] = [
  // Tier I — THE SPARK (1–16)
  'Soundcheck', 'Audition', 'Prelude', 'Newcomer', 'Static', 'Murmur', 'Whisper', 'Hum',
  'Spark', 'Flicker', 'Ember', 'Glow', 'Pulse', 'Heartbeat', 'Demo', 'Lofi',
  // Tier II — THE DEMO (17–32)
  'Garage', 'Rookie', 'Loop', 'Groove', 'Hook', 'Riff', 'Verse', 'Chorus',
  'Bridge', 'Refrain', 'Melody', 'Harmony', 'Bassline', 'Backbeat', 'Mixtape', 'Single',
  // Tier III — THE BUZZ (33–48)
  'Buzz', 'Chatter', 'Local', 'Homegrown', 'Underground', 'Gem', 'Cult', 'Buzzworthy',
  'Repeat', 'Earworm', 'Favorite', 'Tastemaker', 'Trendseed', 'Signal', 'Soundwave', 'Frequency',
  // Tier IV — THE STAGE (49–64)
  'Onstage', 'Soundstage', 'Opener', 'Setlist', 'Livewire', 'Applause', 'Encore', 'Ovation',
  'Wings', 'Footlights', 'Standout', 'Showcase', 'Finale', 'Showtime', 'Marquee', 'Billing',
  // Tier V — THE RISE (65–80)
  'Rising', 'Ascendant', 'Upswing', 'Momentum', 'Skyward', 'Liftoff', 'Climbing', 'Soaring',
  'Updraft', 'Altitude', 'Heights', 'Cloudbreak', 'Stratosphere', 'Jetstream', 'Tailwind', 'Breakthrough',
  // Tier VI — THE BREAKOUT (81–96)
  'Breakout', 'Viral', 'Trending', 'Wildfire', 'Sensation', 'Phenomenon', 'Wave', 'Tidal',
  'Surge', 'Overdrive', 'Hype', 'Crossover', 'Mainstage', 'Festival', 'Roadshow', 'Houseful',
  // Tier VII — THE CHARTS (97–112)
  'Charting', 'Charted', 'Hotlist', 'Frontrunner', 'Smash', 'Topper', 'Streak', 'Hitmaker',
  'Anthem', 'Airplay', 'Rotation', 'Billboard', 'Shortlist', 'Skyrocket', 'Powerhouse', 'Capstone',
  // Tier VIII — THE SPOTLIGHT (113–128)
  'Spotlight', 'Limelight', 'Aura', 'Dazzle', 'Shine', 'Radiance', 'Luminary', 'Beacon',
  'Floodlight', 'Neon', 'Starlight', 'Glitter', 'Sparkle', 'Brilliance', 'Incandescent', 'Afterglow',
  // Tier IX — THE HEADLINER (129–144)
  'Headliner', 'Headline', 'Closer', 'Arena', 'Stadium', 'Megastar', 'Elite', 'VIP',
  'Glamour', 'Paparazzi', 'Feature', 'Cover', 'Nominee', 'Winner', 'Trophy', 'Showstopper',
  // Tier X — GOLD (145–160)
  'Gold', 'Golden', 'Amber', 'Lustre', 'Midas', 'Karat', 'Gilded', 'Heyday',
  'Benchmark', 'Bonanza', 'Bullion', 'Songbird', 'Jackpot', 'Laurels', 'Sunburst', 'Shimmer',
  // Tier XI — PLATINUM (161–176)
  'Platinum', 'Sterling', 'Iridium', 'Rhodium', 'Glint', 'Blaze', 'Quicksilver', 'Mercury',
  'Chrome', 'Titanium', 'Titan', 'Regalia', 'Epoch', 'Paragon', 'Stardom', 'Lodestar',
  // Tier XII — DIAMOND (177–192)
  'Diamond', 'Solitaire', 'Flawless', 'Brilliant', 'Crystal', 'Crystalline', 'Prism', 'Facet',
  'Jewel', 'Sapphire', 'Emerald', 'Ruby', 'Opal', 'Pearl', 'Tiara', 'Era',
  // Tier XIII — THE ICON (193–208)
  'Icon', 'Iconic', 'Zeitgeist', 'Trailblazer', 'Pioneer', 'Original', 'Blueprint', 'Revolution',
  'Influence', 'Muse', 'Idol', 'Maestro', 'Virtuoso', 'Prodigy', 'Mastermind', 'Standard',
  // Tier XIV — THE LEGEND (209–224)
  'Legend', 'Vanguard', 'Legendary', 'Myth', 'Mythic', 'Folklore', 'Lore', 'Storied',
  'Timeless', 'Ageless', 'Enshrined', 'Immortalized', 'Monument', 'Landmark', 'Colossus', 'GOAT',
  // Tier XV — THE COSMOS (225–240)
  'Supernova', 'Nova', 'Comet', 'Meteor', 'Constellation', 'Galaxy', 'Nebula', 'Cosmos',
  'Aurora', 'Eclipse', 'Solstice', 'Zenith', 'Celestial', 'Stardust', 'Firmament', 'Horizon',
  // Tier XVI — ETERNAL (241–256)
  'Eternal', 'Immortal', 'Everlasting', 'Infinite', 'Forever', 'Ethereal', 'Transcendent', 'Sublime',
  'Ascended', 'Apex', 'Pinnacle', 'Summit', 'Crowned', 'Sovereign', 'Peerless', 'POPSTAR',
] as const;

/** A fully-resolved rank for a given level — name + its tier context. */
export interface RankInfo {
  /** The level this rank is for, clamped to [1, 256]. */
  level: number;
  /** The single-word rank name (e.g. "Heartbeat", "Diamond", "POPSTAR"). */
  name: string;
  /** Which tier (1–16) the level falls in. */
  tier: number;
  /** The tier's display name (e.g. "The Spark"). */
  tierName: string;
  /** The tier's roman-numeral label (I … XVI). */
  tierRoman: string;
  /** The tier's flavour tagline. */
  tierTagline: string;
  /** 1-based position of this rank WITHIN its tier (1 … 16). */
  rankInTier: number;
  /** True once the player has reached the final rank (level ≥ 256 → POPSTAR). */
  isMax: boolean;
}

/**
 * Resolve a level into its POPSTAR rank + tier. Levels below 1 clamp to 1; levels
 * at/above 256 clamp to the capstone (POPSTAR), so the helper is total over any
 * integer the unbounded {@link import('./xp').levelForXp} can produce.
 *
 * @param level a 1-based account level (from `levelForXp(xp).level`).
 * @returns the {@link RankInfo} for that level.
 */
export function rankForLevel(level: number): RankInfo {
  const lvl = Number.isFinite(level)
    ? Math.min(MAX_RANK_LEVEL, Math.max(MIN_RANK_LEVEL, Math.floor(level)))
    : MIN_RANK_LEVEL;
  const idx = lvl - 1;
  const tierIdx = Math.floor(idx / RANKS_PER_TIER); // 0..15
  const tier = RANK_TIERS[tierIdx];
  return {
    level: lvl,
    name: RANK_NAMES[idx],
    tier: tier.tier,
    tierName: tier.name,
    tierRoman: tier.roman,
    tierTagline: tier.tagline,
    rankInTier: (idx % RANKS_PER_TIER) + 1,
    isMax: lvl >= MAX_RANK_LEVEL,
  };
}

/** Convenience: just the rank name for a level (e.g. for compact labels). */
export function rankName(level: number): string {
  return rankForLevel(level).name;
}
